import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type RevertStageOption = {
  name: string;
  routingRuleId: string;
  workflowTemplateStageId: string;
  sequenceNumber: number;
  /** Pre-assigned officer when reverting to allocation officer stage. */
  assignedUserId?: string;
  assignedUserName?: string;
};

export type WorkflowTemplateStageUserRow = {
  id: string;
  displayName: string;
};

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function numField(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  }
  return 0;
}

function normalizeStages(raw: unknown): RevertStageOption[] {
  if (!Array.isArray(raw)) return [];
  const out: RevertStageOption[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const routingRuleId = strField(r, 'routing_rule_id', 'routingRuleId');
    if (!routingRuleId) continue;
    out.push({
      name: strField(r, 'name'),
      routingRuleId,
      workflowTemplateStageId: strField(r, 'workflow_template_stage_id', 'workflowTemplateStageId'),
      sequenceNumber: numField(r, 'sequence_number', 'sequenceNumber'),
      assignedUserId: strField(
        r,
        'assigned_user_id',
        'assignedUserId',
        'allocation_officer_id',
        'allocationOfficerId',
        'revert_user_id',
        'revertUserId',
      ),
      assignedUserName: strField(
        r,
        'assigned_user_name',
        'assignedUserName',
        'allocation_officer_name',
        'allocationOfficerName',
        'revert_user_name',
        'revertUserName',
      ),
    });
  }
  return out;
}

function normalizeWorkflowTemplateStageUsers(raw: unknown): WorkflowTemplateStageUserRow[] {
  if (!Array.isArray(raw)) return [];
  const out: WorkflowTemplateStageUserRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const id = strField(r, 'id', 'user_id', 'userId');
    const displayName = strField(r, 'display_name', 'displayName', 'name');
    if (!id) continue;
    out.push({ id, displayName: displayName || id });
  }
  return out;
}

function appCode(input?: string, fallbackEnv?: string): string {
  return (input ?? fallbackEnv ?? 'INTRANET').trim();
}

export function revertStageLabel(stage: RevertStageOption): { id: string; label: string } {
  const name = stage.name.trim();
  const seq = stage.sequenceNumber;
  const label =
    seq > 0 && name ? `Stage ${seq} - ${name}` : name || (seq > 0 ? `Stage ${seq}` : 'Stage');
  return { id: stage.routingRuleId, label };
}

/** True when reverting to the allocation officer stage (re-allocation flow). */
export function isAllocationOfficerRevertStage(stageName: string): boolean {
  const n = stageName.trim().toLowerCase();
  return n.includes('allocation officer') && (n.includes('assigned') || n.includes('assignment'));
}

/**
 * CMS-CRC-29
 * GET /v1/case-requests/revert-stages
 */
export async function listRevertStages(input: {
  userTaskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<RevertStageOption[]>> {
  const userTaskId = input.userTaskId.trim();
  if (!isCaseRequestUuid(userTaskId)) return { success: false, message: 'Invalid user task id.' };

  const applicationCode = appCode(input.applicationCode, import.meta.env.VITE_APP_CODE_REVERT_STAGES ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const res = await caseApiRequest<unknown>(
    `/revert-stages?user_task_id=${encodeURIComponent(userTaskId)}`,
    {
      headers: { applicationCode, 'application-code': applicationCode },
      signal: input.signal,
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeStages(res.data) };
}

/**
 * CMS — active users for a workflow template stage (revert to allocation officer).
 * GET /v1/case-requests/workflow-template-stage-users/active
 */
export async function listWorkflowTemplateStageUsersActive(input: {
  workflowTemplateStageId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<WorkflowTemplateStageUserRow[]>> {
  const workflowTemplateStageId = input.workflowTemplateStageId.trim();
  if (!isCaseRequestUuid(workflowTemplateStageId)) {
    return { success: false, message: 'Invalid workflow template stage id.' };
  }

  const applicationCode = appCode(
    input.applicationCode,
    import.meta.env.VITE_APP_CODE_WORKFLOW_TEMPLATE_STAGE_USERS ?? 'INTRANET',
  );
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const qs = new URLSearchParams({ workflowTemplateStageId });
  const res = await caseApiRequest<unknown>(`/workflow-template-stage-users/active?${qs.toString()}`, {
    headers: { applicationCode, 'application-code': applicationCode },
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeWorkflowTemplateStageUsers(res.data) };
}

/**
 * CMS-CRC-28
 * POST /v1/case-requests/revert-user-task
 */
export async function revertUserTask(input: {
  workflowInstanceStageUserTaskId: string;
  routingRuleId: string;
  revertUserId: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
  reallocationReason?: string;
  applicationCode?: string;
}): Promise<ApiResult<void>> {
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const routingRuleId = input.routingRuleId.trim();
  const revertUserId = input.revertUserId.trim();
  const documentMoverId = (input.documentMoverId ?? '').trim();
  const reallocationReason = (input.reallocationReason ?? '').trim();

  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };
  if (!isCaseRequestUuid(routingRuleId)) return { success: false, message: 'Revert stage is required.' };
  if (!isCaseRequestUuid(revertUserId)) return { success: false, message: 'Select User is required.' };
  if (input.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const applicationCode = appCode(input.applicationCode, import.meta.env.VITE_APP_CODE_REVERT_TASK ?? 'INTRANET');
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const body: Record<string, unknown> = {
    revertUserId,
    documentMoverId: input.physicalFileMovementRequired ? documentMoverId : null,
    workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
    physical_file_movement_required: input.physicalFileMovementRequired,
    routing_rule_id: routingRuleId,
  };
  if (reallocationReason) body.reallocation_reason = reallocationReason;

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>('/revert-user-task', {
    method: 'POST',
    headers: { applicationCode, 'application-code': applicationCode },
    body,
  });

  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}
